import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { LedgerBoardsApi, GetMyLedgerContributionsResponse } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";

/**
 * The MY POINTS breakdown — the caller's own contributions on a ledger board
 * for a period, grouped by source. Auth required; fetch only while the modal
 * is open (pass undefined to disable).
 *
 * periodKey mirrors the standings hook: undefined = server-resolved current,
 * "" = all-time, bounded by the board's start — not the ledger's whole history, "<n>" =
 * an explicit closed period.
 */
export function useGetMyLedgerContributions(
    ledgerBoardId: string | undefined,
    opts?: { periodKey?: string },
) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: ["ledger-my-contributions", ledgerBoardId, opts?.periodKey ?? "current", fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<GetMyLedgerContributionsResponse> => {
            return new LedgerBoardsApi(getConfig(fullToken)).getLedgerBoardsBoardIdMyContributions({
                boardId: ledgerBoardId!,
                ...opts?.periodKey !== undefined ? { periodKey: opts.periodKey } : {},
            });
        },
        enabled: !!ledgerBoardId && !!fullToken,
        staleTime: 30_000,
    });
}
